import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useTVStore from "../store/tvStore.jsx";
import { useTVPlay } from "../hooks/useTVQueries.jsx";
import { tvApi } from "../api/tvApi.jsx";
import {
  isAndroidTV,
  isKioskEnvironment,
  log,
  getKeyInfo,
  createInteractionHandler,
} from "../utils/device.jsx";
import TVAuthModal from "../components/TVAuthModal.jsx";
import ContentDisplay from "../components/ContentDisplay.jsx";
import InteractionPrompt from "../components/InteractionPrompt.jsx";

const TVDisplay = () => {
  const navigate = useNavigate();
  const { tvName } = useParams();

  const [userInteracted, setUserInteracted] = useState(false);
  const [showInteractionPrompt, setShowInteractionPrompt] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(false);
  const [totalCycleCount, setTotalCycleCount] = useState(0);

  // 세션 기반 인증 상태 관리 (브라우저 세션 동안 유지)
  const [sessionAuthenticated, setSessionAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // 로컬 상태로 TV 데이터 관리
  const [currentTVData, setCurrentTVData] = useState(null);
  const [contents, setContents] = useState([]);
  const [music, setMusic] = useState([]);
  const [settings, setSettings] = useState({
    transitionDuration: 1,
    transitionStyle: "fade",
    isBgmEnabled: false,
  });

  // updatedAt 추적을 위한 상태
  const [initialUpdatedAt, setInitialUpdatedAt] = useState(null);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);

  // TV 상태 관리
  const [tvError, setTvError] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [authCodeChanged, setAuthCodeChanged] = useState(false);

  // 생성된 URL 추적 (메모리 정리용)
  const urlCacheRef = useRef(new Set());
  const blobUrlCacheRef = useRef(new Map()); // blob URL 캐시 추가

  // Zustand 상태
  const { clearTvAuth } = useTVStore();

  // TV name으로 조회하는 API 사용
  const {
    data: tvData,
    isLoading: tvLoading,
    error: initialTvError,
    refetch: refetchTvData,
  } = useTVPlay(tvName);

  // 백엔드 Enum을 프론트엔드 형식으로 변환
  const mapServerTypeToFrontend = useCallback((serverType) => {
    if (!serverType) return "";

    const typeMap = {
      FILE: "upload",
      LINK: "url",
      YOUTUBE: "youtube",
      IMAGE: "image",
      VIDEO: "video",
      WEBSITE: "website",
      FADE: "fade",
      LEFT_SLIDE: "slideLeft",
      RIGHT_SLIDE: "slideRight",
      UP_SLIDE: "slideUp",
      DOWN_SLIDE: "slideDown",
      ZOOM_IN: "zoomIn",
      ZOOM_OUT: "zoomOut",
      ROTATE_LEFT: "rotateLeft",
      ROTATE_RIGHT: "rotateRight",
      HORIZONTAL_FLIP: "flipHorizontal",
      VERTICAL_FLIP: "flipVertical",
      BLIND: "blinds",
      CUBE_ROTATION: "cube",
    };

    const typeString =
      typeof serverType === "string" ? serverType : serverType.toString();
    return typeMap[typeString] || typeString.toLowerCase();
  }, []); // useCallback으로 감싸서 함수 재생성 방지

  // 개선된 바이너리 데이터를 URL로 변환
  const convertBinaryDataToUrl = useCallback(
    (binaryData, contentType = "application/octet-stream") => {
      if (!binaryData) {
        return null;
      }

      // 캐시 키 생성 (바이너리 데이터의 해시 대신 길이와 타입으로 간단히 구분)
      const cacheKey = `${typeof binaryData}-${
        Array.isArray(binaryData) ? binaryData.length : binaryData.length
      }-${contentType}`;

      // 이미 변환된 URL이 있으면 재사용
      if (blobUrlCacheRef.current.has(cacheKey)) {
        const cachedUrl = blobUrlCacheRef.current.get(cacheKey);
        return cachedUrl;
      }

      try {
        let uint8Array;

        if (binaryData instanceof Uint8Array) {
          uint8Array = binaryData;
        } else if (binaryData instanceof ArrayBuffer) {
          uint8Array = new Uint8Array(binaryData);
        } else if (Array.isArray(binaryData)) {
          uint8Array = new Uint8Array(binaryData);
        } else if (typeof binaryData === "string") {
          try {
            const binaryString = atob(binaryData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            uint8Array = bytes;
          } catch (e) {
            return null;
          }
        } else {
          return null;
        }

        if (uint8Array.length === 0) {
          return null;
        }

        const blob = new Blob([uint8Array], { type: contentType });
        const objectUrl = URL.createObjectURL(blob);

        // 캐시에 저장
        blobUrlCacheRef.current.set(cacheKey, objectUrl);
        urlCacheRef.current.add(objectUrl);

        return objectUrl;
      } catch (error) {
        return null;
      }
    },
    []
  );

  // 파일 확장자에서 MIME 타입 추론
  const getMimeTypeFromFilename = useCallback(
    (fileName, defaultType = "application/octet-stream") => {
      if (!fileName) return defaultType;

      const ext = fileName.toLowerCase().split(".").pop();

      const audioMimeMap = {
        mp3: "audio/mpeg",
        wav: "audio/wav",
        ogg: "audio/ogg",
        m4a: "audio/mp4",
        aac: "audio/aac",
        flac: "audio/flac",
        wma: "audio/x-ms-wma",
      };

      const imageMimeMap = {
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        gif: "image/gif",
        webp: "image/webp",
        svg: "image/svg+xml",
        bmp: "image/bmp",
      };

      const videoMimeMap = {
        mp4: "video/mp4",
        webm: "video/webm",
        ogg: "video/ogg",
        avi: "video/x-msvideo",
        mov: "video/quicktime",
        wmv: "video/x-ms-wmv",
      };

      return (
        audioMimeMap[ext] ||
        imageMimeMap[ext] ||
        videoMimeMap[ext] ||
        defaultType
      );
    },
    []
  );

  // YouTube URL 감지 함수
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return (
      url.includes("youtube.com") ||
      url.includes("youtu.be") ||
      url.includes("music.youtube.com")
    );
  };

  // TV 데이터 처리 함수 (재사용 가능)
  const processTvData = useCallback(
    (tvData) => {
      try {
        // TV enabled 상태 먼저 확인
        if (tvData.enabled === false) {
          setIsDisabled(true);
          setTvError("비활성화된 현황판입니다.");
          return; // 더 이상 처리하지 않음
        }

        // TV 기본 정보 설정
        setCurrentTVData(tvData);

        // 초기 updatedAt 저장 (최초 로딩 시에만)
        if (!initialUpdatedAt && tvData.updatedAt) {
          setInitialUpdatedAt(tvData.updatedAt);
        }

        // 설정 정보 변환
        const tvSettings = {
          transitionDuration: tvData.transitionDuration || 1,
          transitionStyle:
            mapServerTypeToFrontend(tvData.transitionType) || "fade",
          isBgmEnabled: tvData.isBgmEnabled || false,
        };
        setSettings(tvSettings);

        // 배경음악 데이터 변환
        const processedMusic = (tvData.bgmMappings || [])
          .map((mapping) => {
            const bgm = mapping.bgm;
            if (!bgm) return null;

            const bgmType = mapServerTypeToFrontend(bgm.contentType);
            let musicUrl = bgm.url;

            if (bgm.binaryData && bgmType === "upload") {
              let mimeType = bgm.mimeType || bgm.mineType;
              if (!mimeType) {
                mimeType = getMimeTypeFromFilename(bgm.fileName, "audio/mpeg");
              }
              musicUrl = convertBinaryDataToUrl(bgm.binaryData, mimeType);
            }

            return {
              id: mapping.id,
              mappingId: mapping.id,
              sortOrder: mapping.sortOrder,
              name: mapping.name,
              playDuration: mapping.playDuration || 180,
              volume: mapping.volume || 50,
              bgm: {
                id: bgm.id,
                title: mapping.name,
                type: bgmType,
                contentType: bgm.contentType,
                url: musicUrl,
                fileName: bgm.fileName || null,
                binaryData: bgm.binaryData,
                mimeType: bgm.mimeType || bgm.mineType,
              },
            };
          })
          .filter(Boolean);

        processedMusic.sort((a, b) => a.sortOrder - b.sortOrder);
        setMusic(processedMusic);

        // 컨텐츠 데이터 변환
        const processedContents = (tvData.contentMappings || [])
          .map((mapping) => {
            const content = mapping.content;
            if (!content) return null;

            const contentType = mapServerTypeToFrontend(content.contentType);
            let contentUrl = content.url;

            if (
              content.binaryData &&
              (contentType === "image" || contentType === "video")
            ) {
              let mimeType = content.mimeType || content.mineType;
              if (!mimeType) {
                const defaultMimeType =
                  contentType === "image"
                    ? "image/jpeg"
                    : contentType === "video"
                    ? "video/mp4"
                    : "application/octet-stream";
                mimeType = getMimeTypeFromFilename(
                  content.fileName,
                  defaultMimeType
                );
              }
              contentUrl = convertBinaryDataToUrl(content.binaryData, mimeType);
            }

            let videoType = "upload";
            if (contentType === "video" && isYouTubeUrl(contentUrl)) {
              videoType = "youtube";
            }

            return {
              id: mapping.id,
              mappingId: mapping.id,
              sortOrder: mapping.sortOrder,
              name: mapping.name,
              playDuration: mapping.playDuration || 10,
              isMusicPlay: mapping.isMusicPlay || false,
              content: {
                id: content.id,
                title: mapping.name,
                type: contentType,
                contentType: content.contentType,
                url: contentUrl,
                fileName: content.fileName || null,
                binaryData: content.binaryData,
                mimeType: content.mimeType || content.mineType,
                videoType: videoType,
              },
            };
          })
          .filter(Boolean);

        processedContents.sort((a, b) => a.sortOrder - b.sortOrder);
        setContents(processedContents);

        // 에러 상태 리셋 (정상 데이터인 경우에만)
        setTvError(null);
        setIsDisabled(false);
        setIsDeleted(false);
        setAuthCodeChanged(false);
      } catch (error) {
        setTvError("데이터 처리 중 오류가 발생했습니다.");
      }
    },
    [
      convertBinaryDataToUrl,
      getMimeTypeFromFilename,
      mapServerTypeToFrontend,
      initialUpdatedAt,
    ]
  );

  // TV 상태 확인 함수
  const checkTvUpdates = useCallback(async () => {
    if (!currentTVData || isCheckingUpdate) {
      return;
    }

    try {
      setIsCheckingUpdate(true);

      // TV 상태만 확인 (경량 API 사용)
      const statusResponse = await tvApi.getTvStatus(currentTVData.id);

      if (!statusResponse.success) {
        throw new Error(statusResponse.message || "상태 확인 실패");
      }

      const status = statusResponse.data;

      // TV가 삭제된 경우
      if (!status.exists) {
        setIsDeleted(true);
        setTvError("존재하지 않는 현황판입니다.");
        return;
      }

      // TV가 비활성화된 경우
      if (!status.enabled) {
        setIsDisabled(true);
        setTvError("비활성화된 현황판입니다.");
        return;
      }

      // 인증번호가 변경된 경우
      const currentAuthCode = currentTVData.authenticationCode;
      const newAuthCode = status.authenticationCode;

      // 인증번호가 새로 생성되었거나 변경된 경우 (null에서 값으로, 또는 값이 변경)
      if (currentAuthCode !== newAuthCode) {
        // 인증번호가 새로 생성되었거나 변경된 경우에만 재인증 요구
        if (newAuthCode && newAuthCode !== currentAuthCode) {
          setAuthCodeChanged(true);
          setSessionAuthenticated(false); // 세션 인증 상태 리셋
          setAuthRequired(true);
          setShowAuthModal(true);
          return;
        }
      }

      // updatedAt 변경 확인
      const serverUpdatedAt = status.updatedAt;

      if (initialUpdatedAt !== serverUpdatedAt) {
        // 전체 TV 데이터 다시 조회
        const fullDataResponse = await refetchTvData();

        if (fullDataResponse.data) {
          processTvData(fullDataResponse.data);
          setInitialUpdatedAt(serverUpdatedAt); // 새로운 updatedAt으로 업데이트
        }
      }

      setLastCheckTime(new Date().toISOString());
    } catch (error) {
      // 에러가 발생해도 재생은 계속 진행
    } finally {
      setIsCheckingUpdate(false);
    }
  }, [
    currentTVData,
    initialUpdatedAt,
    isCheckingUpdate,
    refetchTvData,
    processTvData,
  ]);

  // TV 이름 유효성 검사
  useEffect(() => {
    if (!tvName) {
      navigate("/admin");
      return;
    }
  }, [tvName, navigate]);

  // TV 데이터 처리 및 변환 (기존 로직)
  useEffect(() => {
    if (tvData) {
      processTvData(tvData);
    }
  }, [tvData, processTvData]);

  // 컴포넌트 언마운트 시 메모리 정리
  useEffect(() => {
    return () => {
      urlCacheRef.current.forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      urlCacheRef.current.clear();
    };
  }, []);

  // 수정된 인증 확인 로직 - 세션 기반으로 단순화
  const checkTvAuthenticationAsync = useCallback(async () => {
    if (sessionAuthenticated || authChecked || !currentTVData || checkingAuth) {
      return;
    }

    const tvId = currentTVData.id;

    try {
      setCheckingAuth(true);

      const response = await tvApi.getTvAuthentication(tvId);

      if (!response.success) {
        throw new Error(response.message || "인증 여부 확인에 실패했습니다.");
      }

      const needsAuth = response.data;

      setAuthRequired(needsAuth);
      setAuthChecked(true);

      if (needsAuth) {
        setShowAuthModal(true);
      } else {
        setIsAuthenticated(true);
        setSessionAuthenticated(true);
      }
    } catch (error) {
      setAuthRequired(false);
      setIsAuthenticated(true);
      setSessionAuthenticated(true);
      setAuthChecked(true);
    } finally {
      setCheckingAuth(false);
    }
  }, [currentTVData, checkingAuth, tvName, sessionAuthenticated, authChecked]);

  // TV 데이터 로딩 완료 후 인증 확인 (1회만) - isDisabled가 아닌 경우에만
  useEffect(() => {
    if (currentTVData && !authChecked && !checkingAuth && !isDisabled) {
      checkTvAuthenticationAsync();
    }
  }, [
    currentTVData,
    authChecked,
    checkingAuth,
    checkTvAuthenticationAsync,
    isDisabled,
  ]);

  // TV 인증 성공 처리 (PIN 성공 시 music 데이터 강제 초기화)
  const handleAuthSuccess = async (sessionId) => {
    try {
      setShowAuthModal(false);
      setIsAuthenticated(true);
      setSessionAuthenticated(true);
      setAuthCodeChanged(false); // 인증번호 변경 상태 리셋

      // 수정: 인증번호가 변경된 경우에만 새로운 데이터로 업데이트
      // 단순 인증 성공의 경우 데이터 재조회하지 않음 (배경음악 중복 방지)
      if (authCodeChanged) {
        const fullDataResponse = await refetchTvData();
        if (fullDataResponse.data) {
          processTvData(fullDataResponse.data);
        }
      }

      // PIN 성공 시 music 데이터 강제 초기화 (참조 변경으로 ContentDisplay 재초기화 유도)
      if (music && music.length > 0) {
        setMusic([...music]);
      }
    } catch (error) {
      // 에러 처리는 상위에서 처리
    }
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    clearTvAuth();
    navigate("/admin");
  };

  // 전체 사이클 완료 시 호출되는 함수 (updatedAt 체크 로직 추가)
  const handleCycleComplete = useCallback(() => {
    setTotalCycleCount((prev) => {
      const newCount = prev + 1;

      // 마지막 컨텐츠 재생 완료 시 updatedAt 체크
      checkTvUpdates();

      if (!sessionAuthenticated && isAuthenticated) {
        setSessionAuthenticated(true);
      }

      return newCount;
    });
  }, [sessionAuthenticated, isAuthenticated, checkTvUpdates]);

  // 사용자 상호작용 처리 (기존 로직 유지)
  useEffect(() => {
    if (userInteracted) return;

    if (isAndroidTV() || isKioskEnvironment()) {
      log("안드로이드/키오스크 환경 - 자동 상호작용 처리");
      setUserInteracted(true);
      return;
    }

    const promptTimer = setTimeout(() => {
      if (!userInteracted) {
        setShowInteractionPrompt(true);
      }
    }, 3000);

    const handleInteraction = () => {
      setUserInteracted(true);
      setShowInteractionPrompt(false);
      log("사용자 상호작용 감지됨");
    };

    createInteractionHandler(handleInteraction);

    return () => {
      clearTimeout(promptTimer);
    };
  }, [userInteracted]);

  // 키보드 이벤트 처리 (기존 로직 유지)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!userInteracted) {
        setUserInteracted(true);
        setShowInteractionPrompt(false);
      }

      if (isAndroidTV()) {
        const keyInfo = getKeyInfo(event);
        log(`키 입력: ${keyInfo.keyCode} (${keyInfo.key || "unknown"})`);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [userInteracted]);

  // 클릭 이벤트 처리 (기존 로직 유지)
  useEffect(() => {
    const handleClick = () => {
      if (!userInteracted) {
        setUserInteracted(true);
        setShowInteractionPrompt(false);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [userInteracted]);

  // 에러 상태별 UI 렌더링
  if (isDeleted) {
    return (
      <div className="tv-container display-mode">
        <div className="empty-state">
          <div className="empty-icon">❌</div>
          <h2>존재하지 않는 현황판</h2>
          <p>
            요청하신 현황판을 찾을 수 없습니다.
            <br />
            현황판이 삭제되었거나 접근 권한이 없습니다.
          </p>
          <button
            onClick={() => navigate("/admin")}
            style={{
              margin: "20px",
              padding: "10px 20px",
              fontSize: "16px",
              background: "#0984e3",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            관리자 페이지로 이동
          </button>
        </div>
      </div>
    );
  }

  // 비활성화 상태 확인 - 처음 로딩 시와 업데이트 체크 후 모두 적용
  if (isDisabled) {
    return (
      <div className="tv-container display-mode">
        <div className="empty-state">
          <div className="empty-icon">🚫</div>
          <h2>비활성화된 현황판</h2>
          <p>
            현재 이 현황판은 비활성화 상태입니다.
            <br />
            관리자에게 문의하여 현황판을 활성화해주세요.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              margin: "20px",
              padding: "10px 20px",
              fontSize: "16px",
              background: "#0984e3",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            새로고침
          </button>
        </div>
      </div>
    );
  }

  // 에러 처리 (기존 로직)
  if (initialTvError && !tvData) {
    return (
      <div className="tv-container display-mode">
        <div className="empty-state">
          <div className="empty-icon">❌</div>
          <h2>오류 발생</h2>
          <p>
            현황판 데이터를 불러오는 중 오류가 발생했습니다.
            <br />
            잠시 후 다시 시도하거나 관리자에게 문의하세요.
          </p>
          <p style={{ fontSize: "0.8em", opacity: 0.7, marginTop: "20px" }}>
            에러: {initialTvError?.message}
          </p>
        </div>
      </div>
    );
  }

  // 로딩 상태
  if (tvLoading || !tvName || checkingAuth || !currentTVData) {
    return (
      <div className="loading">
        <div className="loading-icon">📺</div>
        <div className="spinner"></div>
        {checkingAuth
          ? "TV 인증 확인 중..."
          : tvLoading
          ? "현황판 정보 로딩 중..."
          : !currentTVData
          ? "현황판 데이터 처리 중..."
          : "현황판 로딩 중..."}
      </div>
    );
  }

  // 인증 모달 표시 (인증이 필요하고 아직 세션에서 인증되지 않은 경우)
  if (showAuthModal && authRequired && !sessionAuthenticated) {
    return (
      <TVAuthModal
        isOpen={showAuthModal}
        onClose={handleAuthModalClose}
        tvId={currentTVData.id}
        tvName={currentTVData?.name}
        onAuthSuccess={handleAuthSuccess}
      />
    );
  }

  // 핵심 수정: 인증이 필요한 경우 인증 완료 전까지는 ContentDisplay 렌더링 안함
  if (authRequired && !sessionAuthenticated) {
    // 인증 확인이 완료되지 않은 경우 로딩 화면
    if (!authChecked) {
      return (
        <div className="loading">
          <div className="loading-icon">🔐</div>
          <div className="spinner"></div>
          TV 인증 확인 중...
        </div>
      );
    }

    // 인증이 필요하지만 아직 인증되지 않은 경우
    return (
      <div className="tv-container display-mode">
        <div className="empty-state">
          <div className="empty-icon">🔐</div>
          <h2>인증 필요</h2>
          <p>
            이 TV를 보기 위해서는 인증이 필요합니다.
            <br />
            관리자에게 문의하거나 새로고침 후 인증을 진행하세요.
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            style={{
              margin: "20px",
              padding: "10px 20px",
              fontSize: "16px",
              background: "#0984e3",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            인증하기
          </button>
        </div>
      </div>
    );
  }

  // 컨텐츠가 없는 경우
  if (!contents || contents.length === 0) {
    return (
      <div className="tv-container display-mode">
        <div className="empty-state">
          <div className="empty-icon">📺</div>
          <h2>{currentTVData?.name || tvName} TV</h2>
          <p>
            이 TV에 등록된 컨텐츠가 없습니다.
            <br />
            관리자에게 컨텐츠 추가를 요청하세요.
          </p>
          <p style={{ fontSize: "0.4em", opacity: 0.7, marginTop: "30px" }}>
            관리자 모드: {window.location.origin}/admin
          </p>
          {process.env.NODE_ENV === "development" && (
            <div
              style={{
                fontSize: "0.3em",
                opacity: 0.5,
                marginTop: "20px",
                background: "rgba(0,0,0,0.1)",
                padding: "10px",
                borderRadius: "5px",
              }}
            >
              Debug: tvData={!!tvData}, contentMappings=
              {tvData?.contentMappings?.length || 0}, processed=
              {contents?.length || 0}, sessionAuth={sessionAuthenticated},
              authChecked={authChecked}, authRequired={authRequired}, music=
              {music?.length || 0}, initialUpdatedAt={initialUpdatedAt},
              lastCheck={lastCheckTime}, isDisabled={isDisabled}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 정상 재생 화면 (인증이 불필요하거나 인증이 완료된 경우에만 렌더링)
  return (
    <div className="tv-container display-mode">
      <ContentDisplay
        contents={contents}
        settings={settings}
        music={music}
        userInteracted={userInteracted}
        onCycleComplete={handleCycleComplete}
        cycleCount={totalCycleCount}
        sessionAuthenticated={sessionAuthenticated}
      />

      {showInteractionPrompt && (
        <InteractionPrompt
          isAndroidTV={isAndroidTV()}
          onInteraction={() => {
            setUserInteracted(true);
            setShowInteractionPrompt(false);
          }}
        />
      )}

      {/* 인증 모달 */}
      <TVAuthModal
        isOpen={showAuthModal}
        onClose={handleAuthModalClose}
        tvId={currentTVData?.id}
        tvName={currentTVData?.name}
        onAuthSuccess={handleAuthSuccess}
      />

      {/* 업데이트 확인 중 표시 (개발 환경에서만) */}
      {process.env.NODE_ENV === "development" && isCheckingUpdate && (
        <div
          style={{
            position: "fixed",
            bottom: "10px",
            left: "10px",
            background: "rgba(0,0,0,0.7)",
            color: "white",
            padding: "5px 10px",
            borderRadius: "5px",
            fontSize: "12px",
            zIndex: 9999,
          }}
        >
          업데이트 확인 중...
        </div>
      )}
    </div>
  );
};

export default TVDisplay;
